// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Price formatting & conversion utilities
// ---------------------------------------------------------------------------
//
// All internal cart/display amounts are in EUROS (float).
// The backend API may return prices in centimes (integer >= 1000).
// These helpers auto-detect the format and convert as needed.
// ---------------------------------------------------------------------------

import type { CartItem, Product } from '@/lib/types';
import { MAX_AMOUNT } from '@/lib/constants';

/** Threshold above which a numeric price is assumed to be in centimes. */
const CENTIMES_THRESHOLD = 1000;

/** Shared Intl formatter for fr-FR EUR currency. */
const currencyFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Shared Intl formatter for plain number (no currency symbol). */
const plainFormatter = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

/**
 * Auto-detect format and convert to euros.
 * If the value is >= 1000 it is assumed to be in centimes (integer from API).
 *
 * @param price - Price in centimes or euros (number or numeric string).
 * @returns Price in euros rounded to 2 decimals.
 */
export function toEuros(price: number | string | null | undefined): number {
  if (price === null || price === undefined || price === '') {
    return 0;
  }

  const num = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(num)) {
    return 0;
  }

  if (num >= CENTIMES_THRESHOLD) {
    return parseFloat((num / 100).toFixed(2));
  }

  return parseFloat(num.toFixed(2));
}

/**
 * Convert euros to centimes (integer).
 *
 * @param euros - Amount in euros.
 * @returns Amount in centimes (rounded integer).
 */
export function toCentimes(euros: number | string | null | undefined): number {
  if (euros === null || euros === undefined || euros === '') {
    return 0;
  }

  const num = typeof euros === 'string' ? parseFloat(euros) : euros;

  if (isNaN(num)) {
    return 0;
  }

  return Math.round(num * 100);
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * Format a price for display using fr-FR EUR locale.
 *
 * @param price - Price in any format (auto-detects centimes vs euros).
 * @param showSymbol - Whether to include the EUR symbol (default true).
 * @returns Localised string, e.g. "12,50 EUR" or "12,50".
 */
export function formatPrice(price: number | string | null | undefined, showSymbol = true): string {
  const euros = toEuros(price);
  return showSymbol ? currencyFormatter.format(euros) : plainFormatter.format(euros);
}

/**
 * Format a price without currency symbol — useful for input fields.
 */
export function formatPricePlain(price: number | string | null | undefined): string {
  return formatPrice(price, false);
}

// ---------------------------------------------------------------------------
// Input parsing
// ---------------------------------------------------------------------------

/**
 * Parse a user-typed string into euros.
 * Handles French comma separator, currency symbols, and auto-detects centimes.
 *
 * @param input - Raw user input string (e.g. "12,50", "12.50", "1250").
 * @returns Amount in euros.
 */
export function parseInput(input: string): number {
  if (!input || input.trim() === '') {
    return 0;
  }

  // Strip currency symbols and whitespace
  let cleaned = input.replace(/[EUR\s]/g, '');

  // French comma -> dot
  cleaned = cleaned.replace(',', '.');

  const num = parseFloat(cleaned);

  if (isNaN(num)) {
    return 0;
  }

  // Auto-detect centimes only when the user entered a plain integer
  if (num >= CENTIMES_THRESHOLD && !cleaned.includes('.')) {
    return toEuros(num);
  }

  return parseFloat(num.toFixed(2));
}

// ---------------------------------------------------------------------------
// Product processing
// ---------------------------------------------------------------------------

/**
 * Convert all price fields of a product from centimes to euros.
 * Returns a new object (no mutation).
 */
export function processProduct(product: Product): Product {
  if (!product) return product;

  const processed = { ...product };

  processed.price = toEuros(processed.price);
  processed.price_display = formatPrice(processed.price);

  const priceFields = ['base_price', 'sale_price'] as const;
  for (const field of priceFields) {
    if (processed[field] !== undefined) {
      (processed as Record<string, unknown>)[field] = toEuros(processed[field]!);
    }
  }

  return processed;
}

/**
 * Process an array of products — converts all prices to euros.
 */
export function processProducts(products: Product[]): Product[] {
  if (!Array.isArray(products)) {
    return [];
  }
  return products.map(processProduct);
}

// ---------------------------------------------------------------------------
// Calculations
// ---------------------------------------------------------------------------

/**
 * Calculate line total for a given quantity and unit price.
 *
 * @param qty - Item quantity.
 * @param price - Unit price (auto-detects centimes vs euros).
 * @returns Total in euros, rounded to 2 decimals.
 */
export function calculateLineTotal(qty: number, price: number | string): number {
  const priceEuros = toEuros(price);
  return parseFloat((qty * priceEuros).toFixed(2));
}

/**
 * Calculate cart totals from an array of cart items.
 *
 * @returns Object with subtotal, tax (placeholder 0) and total in euros.
 */
export function calculateCartTotal(items: CartItem[]): {
  subtotal: number;
  tax: number;
  total: number;
} {
  if (!Array.isArray(items)) {
    return { subtotal: 0, tax: 0, total: 0 };
  }

  const subtotal = items.reduce((sum, item) => {
    return sum + calculateLineTotal(item.quantity, item.price);
  }, 0);

  // TODO: Tax calculation (TVA) when required
  const tax = 0;
  const total = subtotal + tax;

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    tax: parseFloat(tax.toFixed(2)),
    total: parseFloat(total.toFixed(2)),
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Check whether a price value is valid (numeric, non-negative, within max).
 */
export function isValidPrice(price: number | string | null | undefined): boolean {
  if (price === null || price === undefined) {
    return false;
  }

  const num = typeof price === 'string' ? parseFloat(price) : price;
  return !isNaN(num) && num >= 0 && num <= MAX_AMOUNT;
}
